import { type ElementType, lazy, type ReactNode, Suspense } from 'react';

import classNames from 'classnames';

import { AuthorImagesElement } from '../authorImagesElement';
import { BreadcrumbsElement } from '../breadcrumbsElement';
import { ButtonElement } from '../buttonElement';
import { ClientOnly } from '../ClientOnly';
import { ColumnElement } from '../columnElement';
import { ColumnsElement } from '../columnsElement';
import { ContainerElement } from '../containerElement';
import { DividerElement } from '../dividerElement';
import {
  Heading1Element,
  Heading2Element,
  Heading3Element,
  Heading4Element,
  Heading5Element,
  Heading6Element,
} from '../headingElement';
import { IconElement } from '../iconElement';
import { ImageElement } from '../imageElement';
import { ParagraphSerializer } from '../paragraphElement';
import { PostShareElement } from '../postShareElement';
import { RootElement } from '../rootElement';
import { SectionElement } from '../sectionElement';
import { SocialsElement } from '../socialsElement';
import { SwitchElement } from '../switchElement';
import { SerializeTextLeaf } from '../textLeaf';
import { EditableTextSlot } from '../textLeaf/EditableTextSlot';
import { leadingLeafMarks } from '../textLeaf/leadingLeafMarks';
import type { FullRegistryElementProps } from './FullRegistryElement';
import type { GetElementProps } from './Serializer.types';

// Every element type found in 1,826 production card, header, tag and author structures. Other types
// load the full registry on demand, so a structure no longer pulls every element into the page bundle.
const structureComponents: Record<string, ElementType> = {
  authorImages: AuthorImagesElement,
  breadcrumbs: BreadcrumbsElement,
  button: ButtonElement,
  column: ColumnElement,
  columns: ColumnsElement,
  container: ContainerElement,
  divider: DividerElement,
  icon: IconElement,
  image: ImageElement,
  paragraph: ParagraphSerializer,
  postShare: PostShareElement,
  section: SectionElement,
  socials: SocialsElement,
  switch: SwitchElement,
};

const headingComponents: ElementType[] = [
  Heading1Element,
  Heading2Element,
  Heading3Element,
  Heading4Element,
  Heading5Element,
  Heading6Element,
];

const FullRegistryElement = lazy(() => import('./FullRegistryElement'));

let UnmappedElement: ElementType<FullRegistryElementProps> | undefined;

/** For an app that loads the registry chunk before hydrating, so it can server-render the types outside the map. */
export const setUnmappedStructureElement = (component: ElementType<FullRegistryElementProps>) => {
  UnmappedElement = component;
};

/** Renders like `getElement`, but only the types structures use are bundled with the caller. */
export function getStructureElement(props: GetElementProps): ReactNode {
  const { node, rootClassName, rootStyle, children, key, jsonPath } = props;

  if ('text' in node) {
    return <SerializeTextLeaf key={key} leaf={node} />;
  }

  // Mirrors getElement, which a parity spec checks.
  const nodeId = 'attrs' in node && node.attrs && 'id' in node.attrs ? node.attrs.id : undefined;
  const dataAttributes =
    jsonPath || nodeId
      ? {
          ...(jsonPath ? { 'data-json-path': jsonPath.join(',') } : {}),
          ...(nodeId ? { 'data-node-id': String(nodeId) } : {}),
        }
      : undefined;
  const isTextBlock = node.type === 'heading' || node.type === 'paragraph' || node.type === 'button';
  const blockChildren = isTextBlock ? (
    <EditableTextSlot nodeId={nodeId ? String(nodeId) : undefined} marks={leadingLeafMarks(node)}>
      {children}
    </EditableTextSlot>
  ) : (
    children
  );

  if (node.type === 'doc') {
    return (
      <RootElement element={node} className={classNames(rootClassName, 'dream-doc')} style={rootStyle}>
        {children}
      </RootElement>
    );
  }
  if (node.type === 'hardBreak') {
    return <br />;
  }

  const isHeading = node.type === 'heading';
  const Component = isHeading
    ? ((typeof node.attrs?.level === 'number' ? headingComponents[node.attrs.level - 1] : undefined) ?? Heading6Element)
    : structureComponents[node.type];

  if (!Component) {
    if (UnmappedElement) {
      return <UnmappedElement key={key} elementProps={props} />;
    }
    // Client-only so every server response renders the same markup, whether or not the chunk has loaded there.
    return (
      <ClientOnly key={key}>
        {() => (
          <Suspense fallback={null}>
            <FullRegistryElement elementProps={props} />
          </Suspense>
        )}
      </ClientOnly>
    );
  }

  return (
    <Component
      key={key}
      element={node}
      className={isHeading ? 'dream-heading' : classNames(rootClassName, `dream-${node.type}`)}
      attributes={dataAttributes}
      {...(isHeading ? {} : { isLive: node.type === 'socials' })}
    >
      {blockChildren}
    </Component>
  );
}
